import { BleClient } from '@capacitor-community/bluetooth-le'
import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'
import { Geolocation } from '@capacitor/geolocation'

// Keep this identical for every lock screen. The native BLE plugin is a
// singleton: whichever screen initializes it first chooses the permission
// model for the whole process.
const BLE_INIT_OPTIONS = { androidNeverForLocation: true }

const permissionMessage = error => String(error?.message || error || '')
const isPermissionError = error => /permission|denied|not authorized/i.test(permissionMessage(error))

export async function isLocationRequiredForScan() {
  if (Capacitor.getPlatform() !== 'android') return false

  try {
    const { androidSDKVersion } = await Device.getInfo()
    const sdkVersion = Number(androidSDKVersion)
    return Number.isFinite(sdkVersion) && sdkVersion <= 30
  } catch (error) {
    console.warn('[BLE] Could not determine Android version:', error)
    return true
  }
}

export async function getLocationReadiness() {
  if (!(await isLocationRequiredForScan())) return { state: 'not-required', granted: true }

  try {
    const permissions = await Geolocation.checkPermissions()
    if (permissions.location !== 'granted') return { state: 'prompt', granted: false }

    const enabled = await BleClient.isLocationEnabled()
    return enabled
      ? { state: 'ready', granted: true }
      : { state: 'services-disabled', granted: false }
  } catch (error) {
    console.error('[BLE] Location readiness error:', error)
    return { state: isPermissionError(error) ? 'blocked' : 'unavailable', granted: false, error }
  }
}

export async function getBluetoothReadiness({ requestEnable = false } = {}) {
  try {
    await BleClient.initialize(BLE_INIT_OPTIONS)
    let enabled = await BleClient.isEnabled()

    if (!enabled && requestEnable) {
      try {
        await BleClient.requestEnable()
        enabled = await BleClient.isEnabled()
      } catch (error) {
        return { state: 'disabled', enabled: false, error }
      }
    }

    return enabled
      ? { state: 'ready', enabled: true }
      : { state: 'disabled', enabled: false }
  } catch (error) {
    console.error('[BLE] Initialization error:', error)
    return {
      state: isPermissionError(error) ? 'blocked' : 'unavailable',
      enabled: false,
      error,
    }
  }
}

export async function openAppSettings() {
  try {
    await BleClient.openAppSettings()
  } catch (error) {
    console.error('[BLE] Could not open app settings:', error)
  }
}

export async function openBluetoothSettings() {
  try {
    await BleClient.openBluetoothSettings()
  } catch (error) {
    console.error('[BLE] Could not open Bluetooth settings:', error)
  }
}

export async function openLocationSettings() {
  try {
    await BleClient.openLocationSettings()
  } catch (error) {
    console.error('[BLE] Could not open location settings:', error)
  }
}

export async function initBluetooth({ openSettingsIfFail = true } = {}) {
  const readiness = await getBluetoothReadiness({ requestEnable: true })
  if (!readiness.enabled && openSettingsIfFail && readiness.state === 'disabled') await openBluetoothSettings()
  return readiness.enabled
}

export const isBleSupported = async () => {
  if (Capacitor.getPlatform() === 'web') return false
  return (await getBluetoothReadiness()).state === 'ready'
}

export const checkLocationPermission = async () => {
  return (await getLocationReadiness()).granted
}

export const requestLocationPermission = async () => {
  if (!(await isLocationRequiredForScan())) return true

  try {
    const result = await Geolocation.requestPermissions()
    return result.location === 'granted'
  } catch (e) {
    console.error('Location request error:', e)
    return false
  }
}

export async function ensureLocationAccess() {
  const readiness = await getLocationReadiness()
  if (readiness.granted) return { granted: true, state: readiness.state }

  if (readiness.state === 'services-disabled') {
    await openLocationSettings()
    return { granted: false, state: readiness.state, message: 'Turn on Location in your phone settings, then come back.' }
  }

  if (readiness.state === 'blocked') {
    await openAppSettings()
    return { granted: false, state: readiness.state, message: 'Location is blocked for this app. Allow it in Settings, then come back.' }
  }

  const granted = await requestLocationPermission()
  if (granted) return { granted: true, state: 'ready' }

  const after = await getLocationReadiness()
  if (after.state === 'blocked') {
    await openAppSettings()
    return { granted: false, state: 'blocked', message: 'Location is blocked for this app. Allow it in Settings, then come back.' }
  }

  return { granted: false, state: 'denied', message: 'Location access was denied.' }
}

export async function ensureBluetoothAccess() {
  const readiness = await getBluetoothReadiness({ requestEnable: true })
  if (readiness.enabled) return { enabled: true, state: readiness.state }

  if (readiness.state === 'blocked') {
    await openAppSettings()
    return { enabled: false, state: readiness.state, message: 'Bluetooth is blocked for this app. Allow it in Settings, then come back.' }
  }

  if (readiness.state === 'unavailable') {
    return { enabled: false, state: readiness.state, message: 'Bluetooth is not available on this device.' }
  }

  if (Capacitor.getPlatform() === 'ios') {
    await openAppSettings()
    return {
      enabled: false,
      state: 'disabled',
      message: 'Turn on Bluetooth for this app in Settings, or switch Bluetooth on in Control Centre.',
    }
  }

  await openBluetoothSettings()
  return { enabled: false, state: 'disabled', message: 'Turn on Bluetooth, then come back.' }
}
